import type {
  PlaybookId,
  PlaybookView,
} from "@repo/app-types/entities/playbook";
import type { PlaybookViewFilterDefaults } from "@repo/app-types/entities/playbookViewDefaults";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import type { FC } from "react";
import { useUpdatePlaybookViewDefaultsMutation } from "../../api/entities/playbook.js";
import { useAppDispatch } from "../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";

export const SetPlaybookFilterDefaultAction: FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <Button
    className="w-full justify-center"
    size={Size.SMALL}
    variant={ButtonVariant.Secondary}
    onClick={onClick}
  >
    Set default for everyone
  </Button>
);

interface SetPlaybookFilterDefaultConfirmationProps {
  playbookId: PlaybookId;
  view: PlaybookView;
  /** The filters the viewer changed — see `buildPlaybookViewFilterDefaultsPatch`. */
  defaults: PlaybookViewFilterDefaults;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

/**
 * Saves the filters the viewer is looking at as the playbook's default for
 * this kind of page.
 */
export const SetPlaybookFilterDefaultConfirmation: FC<
  SetPlaybookFilterDefaultConfirmationProps
> = ({ playbookId, view, defaults, isOpen, onOpenChange }) => {
  const dispatch = useAppDispatch();
  const [updateViewDefaults, { isLoading: isSaving }] =
    useUpdatePlaybookViewDefaultsMutation();

  const handleConfirm = async () => {
    try {
      await updateViewDefaults({ id: playbookId, view, defaults }).unwrap();

      onOpenChange(false);
      showNotification(dispatch, {
        id: `playbook-filter-default-updated-${playbookId}-${view}`,
        primaryMessage: "Default filters updated for everyone",
        type: NotificationType.SUCCESS,
      });
    } catch {
      showNotification(dispatch, {
        id: `playbook-filter-default-failed-${playbookId}-${view}`,
        primaryMessage: "Couldn't update the default filters",
        type: NotificationType.FAILURE,
      });
    }
  };

  return (
    <SimpleModal
      isOpen={isOpen}
      variant={SimpleModalVariant.Standard}
      title="Set these filters as the default for everyone?"
      subtitle={
        view === "calls"
          ? "Teammates will start with these filters on the Calls pages for this playbook. They can still make changes and reset to this default."
          : "Teammates will start with these filters on the performance pages for this playbook. They can still make changes and reset to this default."
      }
      confirmButtonText="Set default"
      isPrimaryButtonLoading={isSaving}
      onCancel={() => onOpenChange(false)}
      onConfirm={() => void handleConfirm()}
    />
  );
};
